import { useMemo } from 'react';
import { makeStyles, mergeClasses, tokens, typographyStyles } from '@fluentui/react-components';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

import { useTypingRevealHtml } from '@shared/hooks/useTypingRevealHtml.hook';

const ANSWER_TAGS = [
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'br',
  'strong',
  'b',
  'em',
  'u',
  'del',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'span',
];
const TABLE_SCROLLER_ATTRIBUTE = 'data-ai-answer-table-scroll';

const prepareAnswerHtml = (html: string, allowLinks: boolean): string => {
  if (!html.includes('<table') && !allowLinks) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const link of doc.body.querySelectorAll('a')) {
    if (!link.hasAttribute('href')) {
      link.replaceWith(...link.childNodes);
      continue;
    }
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  }
  for (const table of doc.body.querySelectorAll('table')) {
    const scroller = doc.createElement('div');
    scroller.setAttribute(TABLE_SCROLLER_ATTRIBUTE, '');
    table.replaceWith(scroller);
    scroller.append(table);
  }
  return doc.body.innerHTML;
};

const useStyles = makeStyles({
  body: {
    ...typographyStyles.body1,
    '& p': { margin: 0 },
    '& h1, & h2, & h3, & h4, & h5, & h6': { margin: 0 },
    '& h1': { ...typographyStyles.title3 },
    '& h2': { ...typographyStyles.subtitle1 },
    '& h3': { ...typographyStyles.subtitle2 },
    '& h4': { ...typographyStyles.body1Strong },
    '& h5, & h6': { ...typographyStyles.caption1Strong },
    '& ul, & ol': { margin: 0, paddingLeft: '1.25rem' },
    '& > * + *': { marginTop: tokens.spacingVerticalS },
    '& ul': { listStyleType: 'disc' },
    '& ol': { listStyleType: 'decimal' },
    '& li + li': { marginTop: tokens.spacingVerticalXXS },
    '& a': { color: tokens.colorBrandForegroundLink, textDecorationLine: 'underline' },
    [`& [${TABLE_SCROLLER_ATTRIBUTE}]`]: { maxWidth: '100%', overflowX: 'auto' },
    '& table': {
      width: '100%',
      minWidth: '42rem',
      borderCollapse: 'collapse',
    },
    '& th, & td': {
      padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& th': {
      backgroundColor: tokens.colorNeutralBackground3,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  animating: {
    cursor: 'pointer',
    [`& [${TABLE_SCROLLER_ATTRIBUTE}]`]: { overflowX: 'hidden' },
  },
});

type TProps = {
  answer: string;
  animated?: boolean;
  allowLinks?: boolean;
};

const AiAssistantAnswerContent = ({ answer, animated = false, allowLinks = false }: TProps) => {
  const sanitized = useMemo(() => {
    const safeHtml = DOMPurify.sanitize(marked.parse(answer, { async: false, breaks: true, gfm: true }), {
      ALLOWED_TAGS: allowLinks ? [...ANSWER_TAGS, 'a'] : ANSWER_TAGS,
      ALLOWED_ATTR: allowLinks ? ['href'] : [],
      ALLOW_ARIA_ATTR: false,
      ALLOW_DATA_ATTR: false,
    });
    return prepareAnswerHtml(safeHtml, allowLinks);
  }, [answer, allowLinks]);
  const { html: typed, isAnimating, complete } = useTypingRevealHtml(animated ? sanitized : '');
  const rendered = animated ? typed : sanitized;

  const classes = useStyles();
  // An AI edit run with nothing to tell the author returns an empty answer; the card body then holds nothing at all.
  if (!answer.trim()) return null;

  return (
    <div
      onClick={isAnimating ? complete : undefined}
      className={mergeClasses(classes.body, isAnimating && classes.animating)}
      title={isAnimating ? 'Click to skip' : undefined}
      dangerouslySetInnerHTML={{ __html: rendered || ' ' }}
    />
  );
};

export default AiAssistantAnswerContent;
